<template>
  <div class="contents">
    <RfBaseModal
      ref="connectorModal"
      noButtons
      :title="isEditing ? 'Edit action' : 'Add action'"
      width="960"
      @close="resetState"
    >
      <template #body>
        <RfButtonTabs v-if="!isEditing" :active="tab" class="mb-4">
          <RfButtonNew
            v-for="({ value, key }, _, i) in actionTypes"
            :key="key"
            type="plain"
            size="large"
            class="text-black-2! m-0 box-border rounded-none border-0 px-4 py-2"
            :class="{ '[&_.rf-button--container]:font-bold!': i === tab }"
            @click="tab = i"
          >
            {{ value }}
          </RfButtonNew>
        </RfButtonTabs>
        <div class="modal-actions-title">
          <span>Add an action upon</span>
          <v-select
            v-model="interactionType"
            variant="outlined"
            persistentPlaceholder
            hideDetails
            density="compact"
            required
            :items="interactionTypes"
            itemTitle="text"
            height="34"
            class="mr-3 max-w-60"
          />
          <span v-if="showSurveyOptions"> if selected option is </span>
          <v-select
            v-if="showSurveyOptions"
            v-model="surveyOptionSelected"
            persistentPlaceholder
            variant="outlined"
            hideDetails
            density="compact"
            required
            :disabled="selectedWebsiteAction === 'rf_settings_redirect_location'"
            :items="surveyOptions"
            height="34"
            class="max-w-60"
          />
        </div>
        <RfRetentionConnectors
          v-if="connectorsDialog"
          :model="prompt"
          :interactionType="interactionType"
          :surveyOptionSelected="surveyOptionSelectedValue"
          :editIntegration="editIntegrationTemplate"
          :editIntegrationValue="editIntegrationValue"
          :isEditing="isEditing"
          :actions="prompt.actions"
          :thirdParty="thirdPartyData"
          :clientActions="appClientActions"
          :selectedActionTab="actionType"
          :actionGroupClientActions="clientActions"
          newBehaviour
          @selectedWAChanged="v => (selectedWebsiteAction = v)"
          @closeConnectorDialog="close"
          @addIntegration="addIntegration"
          @setActionGroupClientActions="v => $emit('updateClientActions', v)"
        />
      </template>
    </RfBaseModal>
    <RfBaseModal
      ref="advancedArgsModal"
      title="Advanced Arguments"
      width="550"
      :disabledPrimary="advancedArgsBody?.hasError"
      primaryText="Done"
      @primary="submitArgs"
    >
      <template #body>
        <RfAdvancedArguments ref="advancedArgsBody" />
      </template>
    </RfBaseModal>
    <RfBaseModal
      v-if="actionToDelete"
      ref="statusExperimentModal"
      title="Delete action confirmation"
      message="Are you sure you want to delete this action?"
      width="700"
      errorText="Delete action"
      @error="$emit('deleteAction', actionToDelete)"
      @close="actionToDelete = null"
    >
    </RfBaseModal>
  </div>
</template>

<script>
import RfAdvancedArguments from "@/blocks/RfAdvancedArguments.vue";
import RfRetentionConnectors from "@/components/RfPathCreate/RfRetentionActions/RfRetentionConnectors.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfButtonTabs from "@/components/buttons/RfButtonTabs.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import { useActionsStore } from "@/pinia/actionsStore";
import StringUtils from "@/utils/StringUtils";
import {
  fetchValues,
  interactionTypesExtended,
  isActionDisabled,
} from "@/utils/prompts/promptActionHelpers";
import { isSurvey } from "@/utils/prompts/promptHelpers";
import { cloneDeep } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import PromoInteractionConstants from "@/utils/constants/PromoInteractionConstants";

const actionTypes = {
  connectors: { value: "External & API Actions", key: "connectors" },
  client_actions: { value: "Website Actions", key: "client_actions" },
};

export default {
  name: "RfPromptActionsModals",
  components: {
    RfBaseModal,
    RfButtonNew,
    RfButtonTabs,
    RfRetentionConnectors,
    RfAdvancedArguments,
  },
  props: {
    app: { type: Object, default: () => ({}) },
    prompt: { type: Object, default: () => ({}) },
    currLanguage: { type: String, default: null },
    currAppConnectors: { type: Object, default: () => ({}) },
    appClientActions: { type: Array, default: () => [] },
    clientActions: { type: Array, default: () => [] },
    clientActionObjects: { type: Array, default: () => [] },
  },
  setup: props => {
    const connectorModal = ref();
    const interactionType = ref(PromoInteractionConstants.accept.value);
    const surveyOptionSelected = ref("any");
    const selectedWebsiteAction = ref(null);
    const tab = ref(0);
    const connectorsDialog = ref(false);
    const advancedArgsModal = ref();
    const advancedArgsBody = ref();
    const statusExperimentModal = ref();
    const editArgsItem = ref({});
    const actionToDelete = ref({});
    const editIntegrationItem = ref({});
    const editIntegrationValue = ref({});
    const editIntegrationTemplate = ref({});
    const isEditing = ref(false);
    const actionsStore = useActionsStore();
    const connectorActions = computed(() => actionsStore.connectorActions || []);

    onMounted(() => {
      if (!connectorActions.value.length) actionsStore.getConnectorActions(props.app.id);
    });

    return {
      connectorModal,
      interactionType,
      surveyOptionSelected,
      selectedWebsiteAction,
      tab,
      connectorsDialog,
      actionTypes,
      advancedArgsModal,
      advancedArgsBody,
      editArgsItem,
      actionToDelete,
      statusExperimentModal,
      editIntegrationItem,
      isEditing,
      editIntegrationValue,
      editIntegrationTemplate,
      connectorActions,
      actionType: computed(
        () => (Object.values(actionTypes)?.[tab.value] || actionTypes.connectors).key,
      ),
      model: computed(() => props.prompt),
      actions: computed(() => props.prompt.actions),
      interactionTypes: computed(() => interactionTypesExtended(props.prompt, props.app)),
    };
  },
  computed: {
    surveyOptionSelectedValue() {
      return this.showSurveyOptions ? this.surveyOptionSelected : "any";
    },
    surveySelected() {
      return isSurvey(this.prompt);
    },
    surveyOptions() {
      const options = ["any"];
      const optionsTotal = +(this.prompt.actions.rf_retention_survey_options_total || 3);
      if (this.surveySelected) {
        for (let i = 1; i <= optionsTotal; i += 1) {
          options.push(this.prompt.actions[`rf_retention_survey_option_${i}_value`]);
        }
      }
      return options;
    },
    optionsTotal() {
      return +(this.prompt.actions?.rf_retention_survey_options_total || 3);
    },
    showSurveyOptions() {
      return this.isAccept && this.surveySelected;
    },
    isAccept() {
      return PromoInteractionConstants.accept.value === this.interactionType;
    },
    thirdPartyData() {
      const providers = {
        custom_provider: { name: "Custom_provider", actions: [] },
        redfast: { name: "Recurly Engage", actions: [] },
        sendgrid: { name: "Sendgrid", actions: [] },
        salesforce: { name: "Salesforce", actions: [] },
        braze: { name: "Braze", actions: [] },
        stripe: { name: "Stripe", actions: [] },
        vindicia: { name: "Vindicia", actions: [] },
        braintree: { name: "Braintree", actions: [] },
        zendesk: { name: "Zendesk", actions: [] },
        shopify: { name: "Shopify", actions: [] },
        active_campaign: { name: "ActiveCampaign", actions: [] },
        recurly_commerce: { name: "Recurly Commerce", actions: [] },
      }; // preserve order of providers
      if (!this.connectorActions.length) {
        return providers;
      }
      this.connectorActions.forEach(connectorAction => {
        const providerName = connectorAction.provider;
        const existingProvider = providers[providerName];
        const item = this.createThirdPartyItem(connectorAction);
        if (existingProvider) {
          existingProvider.actions.push(item);
        } else {
          const actions = [item];
          providers[providerName] = {
            name: StringUtils.capitalize(providerName),
            actions,
          };
        }
      });
      for (const provider in providers) {
        if (!providers[provider].actions.length) {
          delete providers[provider];
        }
      }
      return providers;
    },
  },
  methods: {
    editIntegration(actionData) {
      if (actionData.id === "redirect") {
        this.interactionType = actionData.interaction;
        this.editIntegrationValue = { actionName: actionData.actionName, value: actionData.value };
        this.isEditing = true;
        this.tab = 1;
        this.connectorModal.show?.();
        this.connectorsDialog = true;
        return;
      }
      this.interactionType = actionData.interaction_type;
      this.surveyOptionSelected = actionData.survey_option || "any";
      this.editIntegrationItem = actionData;
      this.editIntegrationTemplate = this.createThirdPartyItem(actionData.connector_action);
      this.editIntegrationValue = cloneDeep(actionData.args);
      this.isEditing = true;
      this.show();
    },
    closeDelete() {
      this.actionToDelete = null;
      this.$nextTick(() => this.statusExperimentModal?.close?.());
    },
    deleteAction(value) {
      this.actionToDelete = value;
      this.$nextTick(() => this.statusExperimentModal?.show?.());
    },
    editArgs(action) {
      this.editArgsItem = action;
      const fieldData = (this.clientActionObjects || []).find(
        ({ id }) => id === action.connector_action.id,
      )?.arg_metadata;

      this.advancedArgsModal.show?.();
      this.$nextTick(() =>
        this.advancedArgsBody.init?.(
          fieldData && !Object.keys(action.args || []).length ? [""] : action.args,
          fieldData,
        ),
      );
    },
    submitArgs() {
      this.$emit("updateClientActions", {
        ...this.editArgsItem,
        args: this.advancedArgsBody.get?.(),
      });
      this.advancedArgsModal.close?.();
      this.editArgsItem = {};
    },
    show(action) {
      this.connectorModal.show?.();
      this.connectorsDialog = true;
      this.interactionType = action;
    },
    resetState() {
      this.connectorsDialog = false;
      this.isEditing = false;
      this.tab = 0;
      this.editIntegrationValue = {};
      this.editIntegrationTemplate = {};
    },
    close() {
      this.connectorModal.close?.();
      this.resetState();
    },
    addIntegration(integrationValue, selectedIntegration) {
      const isClient = this.actionType === actionTypes.client_actions.key;
      //client actions only
      if (isClient && this.surveyOptionSelectedValue !== "any") {
        integrationValue = {
          ...integrationValue,
          survey_option_selected: this.surveyOptionSelectedValue,
        };
      }

      let action;

      if (this.isEditing) {
        action = {
          args: integrationValue,
          connector_action: this.editIntegrationItem.connector_action,
          interaction_type: this.interactionType,
          id: this.editIntegrationItem.id,
        };
      } else {
        const connector = this.connectorActions.find(
          a =>
            selectedIntegration.operation === a.operation &&
            selectedIntegration.provider === a.provider &&
            selectedIntegration.description === a.args.description,
        );
        action = {
          args: integrationValue,
          connector_action: connector,
          interaction_type: this.interactionType,
        };
      }
      if (this.surveySelected && !isClient && this.isAccept) {
        action.survey_option = this.surveyOptionSelectedValue;
      }
      if (action.survey_option === "any") action.survey_option = null;

      this.$emit("updateClientActions", action);
      this.interactionType = PromoInteractionConstants.accept.value;
      this.surveyOptionSelected = "any";
      this.editIntegrationItem = {};
      this.editIntegrationValue = {};
      this.editIntegrationTemplate = {};
      this.close();
    },
    createThirdPartyItem(connectorAction) {
      const providerName = connectorAction.provider;
      const { args } = connectorAction;
      return {
        provider: providerName,
        operation: connectorAction.operation,
        description: args.description,
        disabled: isActionDisabled(
          args,
          this.interactionType,
          this.prompt,
          this.app.providers,
          this.interactionTypes,
        ),
        inputs: args.inputs.map(input => {
          return {
            valueDescription: input.value_description,
            type: input.input_type,
            hideId: input.hide_id,
            values: fetchValues(input, this.currAppConnectors, this.prompt, this.currLanguage),
            valueLabel: input.value_label,
            counter: input.character_counter,
            defaultValue: input.default_value,
            placeholder: input.value,
            isRequired: !input.optional,
          };
        }),
      };
    },
  },
};
</script>
